import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createBlock as _createBlock, createVNode as _createVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, vModelCheckbox as _vModelCheckbox, withModifiers as _withModifiers, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  id: "gate-login-title",
  class: "gate-login__title"
}
const _hoisted_2 = { class: "gate-login__field" }
const _hoisted_3 = { class: "gate-login__field" }
const _hoisted_4 = { class: "gate-login__auto" }
const _hoisted_5 = ["disabled"]
const _hoisted_6 = {
  key: 3,
  class: "gate-login__links"
}

import { computed, nextTick, onMounted, reactive, ref } from 'vue'
import KakaoLoginButton from '@/components/auth/KakaoLoginButton.vue'
import PublicLoginKindChoice from '@/components/auth/PublicLoginKindChoice.vue'
import { startKakaoLogin } from '@/composables/useKakaoOAuth'
import { memberHomeAfterLogin } from '@/composables/useLoginDestination'
import { useAdminAuth } from '@/composables/useAdminAuth'
import { useMemberAuth } from '@/composables/useMemberAuth'
import { useSellerAuth } from '@/composables/useSellerAuth'
import {
  decidePublicLoginKind,
  identifyPublicLogin,
  type PublicLoginKind,
} from '@/composables/useUnifiedLogin'
import { loadAutoLogin } from '@/constants/autoLogin'
import {
  inputValidationState,
  isLoginPasswordValid,
  isLoginUserIdValid,
  sanitizeLoginUserIdInput,
} from '@/utils/formValidation'


export default /*@__PURE__*/_defineComponent({
  __name: 'GateLoginPanel',
  props: {
    context: { default: 'gate' },
    redirectTo: { default: '' }
  },
  emits: ["back", "success"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const { login: loginMember } = useMemberAuth()
const { login: loginSeller } = useSellerAuth()
const { login: loginAdmin } = useAdminAuth()
const userIdRef = ref<HTMLInputElement | null>(null)
const form = reactive({
  userId: '',
  password: '',
})
const rememberMe = ref(loadAutoLogin('member') || loadAutoLogin('seller') || loadAutoLogin('admin'))
const isSubmitting = ref(false)
const kakaoBusy = ref(false)
const pendingKinds = ref<PublicLoginKind[] | null>(null)

const needsKindChoice = computed(() => Boolean(pendingKinds.value))

const userIdInputState = computed(() =>
  inputValidationState(form.userId, isLoginUserIdValid(form.userId)),
)
const passwordInputState = computed(() =>
  inputValidationState(form.password, isLoginPasswordValid(form.password)),
)

function onUserIdInput(event: Event) {
  form.userId = sanitizeLoginUserIdInput((event.target as HTMLInputElement).value)
  pendingKinds.value = null
}

function onResetKind() {
  pendingKinds.value = null
}

async function onKakaoLogin() {
  if (kakaoBusy.value || isSubmitting.value) return
  kakaoBusy.value = true
  try {
    await startKakaoLogin({
      remember: rememberMe.value,
      redirect: props.redirectTo || memberHomeAfterLogin(),
    })
  } catch (error) {
    kakaoBusy.value = false
    window.alert(error instanceof Error ? error.message : '카카오 로그인을 시작하지 못했습니다.')
  }
}

async function enterAs(kind: PublicLoginKind) {
  if (kind === 'seller') {
    await loginSeller(form.userId, form.password, rememberMe.value)
  } else if (kind === 'admin') {
    await loginAdmin(form.userId, form.password, rememberMe.value)
  } else {
    await loginMember(form.userId, form.password, rememberMe.value)
  }
  emit('success')
}

async function onPickKind(kind: PublicLoginKind) {
  if (isSubmitting.value) return
  isSubmitting.value = true
  try {
    await enterAs(kind)
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '로그인에 실패했습니다.')
  } finally {
    isSubmitting.value = false
  }
}

async function onSubmit() {
  if (isSubmitting.value) return
  if (!isLoginUserIdValid(form.userId)) {
    window.alert('아이디 형식을 확인해 주세요.')
    return
  }
  if (!isLoginPasswordValid(form.password)) {
    window.alert('비밀번호를 입력해 주세요.')
    return
  }

  isSubmitting.value = true
  try {
    const kinds = await identifyPublicLogin(form.userId, form.password)
    const decision = decidePublicLoginKind(kinds, false)
    if (decision === 'choose') {
      pendingKinds.value = kinds
      return
    }
    await enterAs(decision)
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '로그인에 실패했습니다.')
  } finally {
    isSubmitting.value = false
  }
}

onMounted(() => {
  void nextTick(() => userIdRef.value?.focus())
})

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["gate-login", `gate-login--${props.context}`])
  }, [
    (props.context === 'gate')
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          type: "button",
          class: "gate-login__back",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('back')))
        }, " ← 사업구분 선택 "))
      : _createCommentVNode("", true),
    _createElementVNode("h2", _hoisted_1, _toDisplayString(props.context === 'mall' ? '함우리몰 로그인' : '로그인'), 1),
    (needsKindChoice.value)
      ? (_openBlock(), _createBlock(PublicLoginKindChoice, {
          key: 1,
          kinds: pendingKinds.value || [],
          busy: isSubmitting.value,
          onPick: onPickKind,
          onReset: onResetKind
        }, null, 8, ["kinds", "busy"]))
      : (_openBlock(), _createElementBlock("form", {
          key: 2,
          class: "gate-login__form",
          onSubmit: _withModifiers(onSubmit, ["prevent"])
        }, [
          _createVNode(KakaoLoginButton, {
            busy: kakaoBusy.value,
            onClick: onKakaoLogin
          }, null, 8, ["busy"]),
          _cache[7] || (_cache[7] = _createElementVNode("p", { class: "gate-login__split" }, "또는 아이디로 로그인", -1)),
          _createElementVNode("label", _hoisted_2, [
            _cache[4] || (_cache[4] = _createElementVNode("span", null, "아이디", -1)),
            _withDirectives(_createElementVNode("input", {
              ref_key: "userIdRef",
              ref: userIdRef,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.userId) = $event)),
              type: "text",
              autocomplete: "username",
              placeholder: "아이디를 입력해 주세요",
              maxlength: "40",
              class: _normalizeClass(userIdInputState.value),
              onInput: onUserIdInput
            }, null, 34), [
              [_vModelText, form.userId]
            ])
          ]),
          _createElementVNode("label", _hoisted_3, [
            _cache[5] || (_cache[5] = _createElementVNode("span", null, "비밀번호", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.password) = $event)),
              type: "password",
              autocomplete: "current-password",
              placeholder: "비밀번호를 입력해 주세요",
              class: _normalizeClass(passwordInputState.value)
            }, null, 2), [
              [_vModelText, form.password]
            ])
          ]),
          _createElementVNode("label", _hoisted_4, [
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((rememberMe).value = $event)),
              type: "checkbox"
            }, null, 512), [
              [_vModelCheckbox, rememberMe.value]
            ]),
            _cache[6] || (_cache[6] = _createElementVNode("span", null, "자동 로그인", -1))
          ]),
          _createElementVNode("button", {
            type: "submit",
            class: "gate-login__submit",
            disabled: isSubmitting.value
          }, _toDisplayString(isSubmitting.value ? '로그인 중...' : '로그인'), 9, _hoisted_5)
        ], 32)),
    (!needsKindChoice.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createVNode(_component_router_link, { to: "/find-id" }, {
            default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
              _createTextVNode("아이디 찾기", -1)
            ]))]),
            _: 1
          }),
          _cache[11] || (_cache[11] = _createElementVNode("span", { "aria-hidden": "true" }, null, -1)),
          _createVNode(_component_router_link, { to: "/find-password" }, {
            default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
              _createTextVNode("비밀번호 찾기", -1)
            ]))]),
            _: 1
          }),
          _cache[12] || (_cache[12] = _createElementVNode("span", { "aria-hidden": "true" }, null, -1)),
          _createVNode(_component_router_link, {
            to: props.context === 'mall' ? '/mall/signup' : '/signup'
          }, {
            default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
              _createTextVNode("회원가입", -1)
            ]))]),
            _: 1
          }, 8, ["to"])
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})